'use client';

import { useCallback } from 'react';
import { useAgentRun } from '@/lib/agent-kit/useAgentRun';
import { resumeRefineStepOrder } from './step-graph';
import type {
  ResumeRefineResult,
  ResumeRefineStepKey,
  ResumeRefineTrace,
} from './types';

/**
 * Client hook for the streaming refine endpoint.
 *
 * Thin wrapper over the kit hook: it only pins the endpoint and the step keys,
 * so the refine flow cannot drift out of sync with its own graph. Steps start as
 * the full PENDING plan, so the checklist renders complete and greys in.
 *
 * The caller keeps its existing non-streaming path untouched if it wants to —
 * the route serves both. Using this hook is what opts into the step UI.
 */
export function useResumeRefine(reportId: string) {
  const { run, ...state } = useAgentRun<ResumeRefineStepKey, ResumeRefineResult>(
    `/api/resume-builder/${reportId}/refine`,
    resumeRefineStepOrder,
  );

  /**
   * Resolves to the refined resume, or null when the run failed or was aborted
   * — the error is already on `state.error`, so callers that only need to know
   * whether to reload can branch on the null.
   */
  const refine = useCallback(
    (payload: { instructions?: string } = {}) => run(payload),
    [run],
  );

  /**
   * The kit's `trace` is only typed as far as the kit knows — it cannot see this
   * flow's `detail`. Reading it back off the result recovers the real type, so
   * the caller can hand it straight to ResumeRefineProgress.
   */
  const trace: ResumeRefineTrace | null = state.result?.trace ?? null;

  return { ...state, trace, refine };
}
